import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { NEVER_RE, extractCodes, normalizeRule, ruleMentions } from "@/lib/codingRules";
import { titleCase } from "@/components/practice-setup/psPrimitives";

const INK = "#141412";
const INDIGO = "#5856D6";
const HAIR = "#E9E9EC";
const GREY = "#6E6E6B";
const RED = "#C4302A";
const AMBER = "#9A5A00";

function codeSetOf(cpts, icds) {
  return new Set([...(cpts || []).map((c) => c.code), ...(icds || []).map((d) => d.code)]);
}

/* Shared composer: one input + Always|Never switch, live duplicate/conflict check,
   tap-to-insert code tokens. Return saves. No Save button. */
export function RuleComposer({ providerName, cpts = [], icds = [], rules, tokensMode = "collapsed", onAdd, onAttempt, saving, err, prefill }) {
  const [text, setText] = useState("");
  const [kind, setKind] = useState("always");
  const [kindTouched, setKindTouched] = useState(false);
  const [locked, setLocked] = useState(false);
  const [tokensOpen, setTokensOpen] = useState(tokensMode === "always");
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);

  // Prefill from a line/diagnosis inspector (switch set + locked).
  useEffect(() => {
    if (!prefill || !prefill.nonce) return;
    setText(prefill.text || "");
    setKind(prefill.kind || "always");
    setKindTouched(true);
    setLocked(!!prefill.locked);
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) { el.focus(); const p = (prefill.text || "").length; el.setSelectionRange(p, p); }
    });
  }, [prefill]);

  const setKindFromText = (v) => { if (!kindTouched && !locked) setKind(NEVER_RE.test(v) ? "never" : "always"); };
  const onType = (v) => { setText(v); setKindFromText(v); };

  const norm = normalizeRule(text);
  const duplicate = useMemo(() => {
    if (!norm) return null;
    const hitInc = (rules?.inc || []).find((r) => normalizeRule(r) === norm);
    if (hitInc) return { kind: "Include", text: hitInc };
    const hitExc = (rules?.exc || []).find((r) => normalizeRule(r) === norm);
    if (hitExc) return { kind: "Exclude", text: hitExc };
    return null;
  }, [norm, rules]);

  const conflict = useMemo(() => {
    if (!norm || duplicate) return null;
    const newCodes = extractCodes(text).codes;
    if (!newCodes.length) return null;
    const opposite = kind === "always" ? (rules?.exc || []) : (rules?.inc || []);
    for (const r of opposite) {
      const shared = extractCodes(r).codes.find((c) => newCodes.includes(c));
      if (shared) return { code: shared, text: r, other: kind === "always" ? "Exclude" : "Include" };
    }
    return null;
  }, [norm, duplicate, text, kind, rules]);

  const insertCode = (code) => {
    const el = inputRef.current;
    if (!el) { const next = text ? `${text} ${code} ` : `${code} `; setText(next); setKindFromText(next); return; }
    const s = el.selectionStart ?? text.length, e = el.selectionEnd ?? text.length;
    const before = text.slice(0, s), after = text.slice(e);
    const pre = before && !/\s$/.test(before) ? " " : "";
    const post = after && !/^\s/.test(after) ? " " : (after ? "" : " ");
    const insert = `${pre}${code}${post}`;
    const next = before + insert + after;
    setText(next); setKindFromText(next);
    requestAnimationFrame(() => { el.focus(); const p = (before + insert).length; el.setSelectionRange(p, p); });
  };

  const handleSave = async () => {
    const t = text.trim();
    if (!t || saving || duplicate) return;
    onAttempt?.({ text: t, kind });
    const ok = await onAdd?.(t, kind);
    if (ok) {
      toast.success(`Saved to ${titleCase(providerName)}'s pre-claim rules — this claim doesn't change`);
      setText(""); setKind("always"); setKindTouched(false); setLocked(false);
    }
    inputRef.current?.focus();
  };

  const onKeyDown = (e) => { if (e.key === "Enter") { e.preventDefault(); handleSave(); } };

  const tokens = [...cpts.map((c) => ({ ...c, kind: "cpt" })), ...icds.map((d) => ({ ...d, kind: "icd" }))];
  const check = err
    ? { color: RED, text: err }
    : duplicate
      ? { color: RED, text: `Already a rule: ${duplicate.kind} · "${duplicate.text}"` }
      : conflict
        ? { color: AMBER, text: `A ${conflict.other} rule also mentions ${conflict.code}: "${conflict.text}" — check they don't contradict` }
        : null;

  return (
    <div data-testid="cfv-rule-composer">
      <div style={{ display: "flex", alignItems: "stretch", border: `1px solid ${focused ? INDIGO : HAIR}`, borderRadius: 12, background: "#fff", overflow: "hidden", transition: "border-color .15s" }}>
        <input
          ref={inputRef}
          data-testid="cfv-rule-input"
          value={text}
          onChange={(e) => onType(e.target.value)}
          onKeyDown={onKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder="Add a rule — e.g. Exclude 93350 without a documented indication"
          style={{ flex: 1, minWidth: 0, border: "none", outline: "none", background: "transparent", padding: "10px 12px", fontSize: 13, color: INK }}
        />
        <div style={{ display: "flex", borderLeft: `1px solid ${HAIR}` }}>
          {[["always", "Include"], ["never", "Exclude"]].map(([k, label]) => {
            const on = kind === k;
            return (
              <button key={k} type="button" data-testid={`cfv-rule-kind-${k}`}
                onClick={() => { if (locked) return; setKind(k); setKindTouched(true); }}
                disabled={locked}
                style={{ fontSize: 11.5, fontWeight: 600, padding: "0 13px", border: "none", cursor: locked ? "default" : "pointer", background: on ? INK : "transparent", color: on ? "#fff" : INK, opacity: locked && !on ? 0.4 : 1 }}>
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <div aria-live="polite" data-testid="cfv-rule-check" style={{ minHeight: check ? undefined : 0, marginTop: check ? 6 : 0, fontSize: 11.5, color: check?.color }}>
        {check?.text || ""}
      </div>

      {tokens.length ? (
        tokensMode === "collapsed" && !tokensOpen ? (
          <button type="button" data-testid="cfv-rule-tokens-toggle" onClick={() => setTokensOpen(true)}
            style={{ marginTop: 8, fontSize: 11.5, fontWeight: 600, color: INDIGO, background: "none", border: "none", padding: 0, cursor: "pointer" }}>
            Insert a code from this claim
          </button>
        ) : (
          <div style={{ marginTop: 8 }}>
            {tokensMode === "always" ? <div style={{ fontSize: 10.5, color: GREY, marginBottom: 5 }}>Codes on this claim — tap to insert</div> : null}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {tokens.map((t, i) => (
                <button key={`${t.code}-${i}`} type="button" data-testid={`cfv-rule-token-${t.code}`}
                  title={t.description || t.code} onClick={() => insertCode(t.code)}
                  style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 11, border: `1px solid ${HAIR}`, borderRadius: 6, padding: "2px 7px", background: "#fff", color: INK, cursor: "pointer" }}>
                  {t.code}
                </button>
              ))}
            </div>
          </div>
        )
      ) : null}
    </div>
  );
}

function NotInSetup({ providerName, onManage }) {
  return (
    <div data-testid="cfv-rules-not-in-setup" style={{ fontSize: 12.5, color: GREY, lineHeight: 1.5 }}>
      {titleCase(providerName) || "This provider"} isn't in Practice Setup yet, so there's nowhere to save rules for this NPI.{" "}
      <button type="button" onClick={onManage} style={{ color: INDIGO, fontWeight: 600, background: "none", border: "none", padding: 0, cursor: "pointer" }}>Add in Practice Setup ›</button>
    </div>
  );
}

/* Verdict quick-add (rail default). Tokens collapsed. Stays on the verdict after save. */
export function CodingRulesVerdict({ rr, provName, cpts, icds, onAllRules, onManage, prefill, onAttempt }) {
  const [hl, setHl] = useState(null);
  if (!rr.inList) {
    return (
      <div style={{ marginTop: 18 }}>
        <div className="cfv-rail-lbl" style={{ marginBottom: 8 }}>Pre-Claim Rules</div>
        <NotInSetup providerName={provName} onManage={onManage} />
      </div>
    );
  }
  const set = codeSetOf(cpts, icds);
  const mentions = [
    ...rr.inc.map((t) => ({ t, type: "Include" })),
    ...rr.exc.map((t) => ({ t, type: "Exclude" })),
  ].filter((r) => ruleMentions(r.t, set));
  const total = rr.inc.length + rr.exc.length;
  const onAdd = async (text, kind) => { const ok = await rr.add(text, kind); if (ok) setHl(normalizeRule(text)); return ok; };

  return (
    <div style={{ marginTop: 18 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
        <div className="cfv-rail-lbl" style={{ margin: 0 }} data-testid="cfv-rules-verdict-head">Pre-Claim Rules · {titleCase(provName)}</div>
        <button type="button" data-testid="cfv-rules-all" onClick={onAllRules} style={{ fontSize: 11.5, fontWeight: 600, color: INDIGO, background: "none", border: "none", padding: 0, cursor: "pointer" }}>All rules ›</button>
      </div>

      {mentions.length ? (
        <div style={{ marginBottom: 10, display: "flex", flexDirection: "column", gap: 4 }}>
          {mentions.map((r, i) => (
            <div key={i} data-testid={`cfv-rules-mention-${i}`} style={{ fontSize: 12, color: INK, background: hl && normalizeRule(r.t) === hl ? "rgba(88,86,214,.08)" : "transparent", borderRadius: 6, padding: hl && normalizeRule(r.t) === hl ? "2px 6px" : 0, transition: "background .8s" }}>
              <span style={{ color: GREY, fontWeight: 600 }}>{r.type} · </span>{r.t}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ fontSize: 12, color: GREY, marginBottom: 10 }}>
          {total ? `${total} saved rule${total === 1 ? "" : "s"} · none mention codes on this claim` : "No rules yet — add one while you work"}
        </div>
      )}

      <RuleComposer providerName={provName} cpts={cpts} icds={icds} rules={{ inc: rr.inc, exc: rr.exc }} tokensMode="collapsed" onAdd={onAdd} onAttempt={onAttempt} saving={rr.saving} err={rr.err} prefill={prefill} />

      <div style={{ fontSize: 11, color: GREY, marginTop: 8 }}>Saved for all of {titleCase(provName)}'s future claims — this claim doesn't change.</div>
    </div>
  );
}

function RuleBand({ label, list, isInc, onFlip, onDel, hl }) {
  if (!list.length) return null;
  return (
    <div style={{ marginTop: 12 }} data-testid={`cfv-rules-band-${label.toLowerCase()}`}>
      <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: GREY, marginBottom: 6 }}>{label}</div>
      <div style={{ border: `1px solid ${HAIR}`, borderRadius: 12, overflow: "hidden" }}>
        {list.map((r, i) => (
          <div key={i} data-testid={`cfv-rules-row-${isInc ? "inc" : "exc"}-${i}`} className="cfv-rule-row"
            style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 11px", borderTop: i ? `1px solid ${HAIR}` : "none", background: hl && normalizeRule(r) === hl ? "rgba(88,86,214,.08)" : "#fff", transition: "background .8s" }}>
            <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: INK }}>{r}</span>
            <button type="button" title={`Switch to ${isInc ? "Exclude" : "Include"}`} data-testid={`cfv-rules-flip-${isInc ? "inc" : "exc"}-${i}`}
              onClick={() => onFlip(isInc, i)}
              style={{ fontSize: 9.5, fontWeight: 600, padding: "2px 8px", borderRadius: 6, background: "#F1F1F4", color: GREY, border: "none", cursor: "pointer", flex: "none" }}>
              {isInc ? "Include" : "Exclude"}
            </button>
            <button type="button" title="Delete rule" data-testid={`cfv-rules-del-${isInc ? "inc" : "exc"}-${i}`}
              onClick={() => onDel(isInc, i)}
              className="cfv-rule-del"
              style={{ background: "none", border: "none", cursor: "pointer", color: GREY, display: "flex", flex: "none" }}>
              <X size={13} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* Rendering-provider full rules panel (tile → rail). Tokens always visible. */
export function CodingRulesProviderPanel({ rr, provName, cpts, icds, onManage, onAttempt }) {
  const [hl, setHl] = useState(null);
  if (!rr.inList) return <NotInSetup providerName={provName} onManage={onManage} />;
  const onAdd = async (text, kind) => { const ok = await rr.add(text, kind); if (ok) setHl(normalizeRule(text)); return ok; };

  return (
    <div data-testid="cfv-rules-provider-panel">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
        <div className="cfv-rail-lbl" style={{ margin: 0 }}>Pre-Claim Rules</div>
        <button type="button" data-testid="cfv-rules-manage" onClick={onManage} style={{ fontSize: 11.5, fontWeight: 600, color: INDIGO, background: "none", border: "none", padding: 0, cursor: "pointer" }}>Manage in Practice Setup ›</button>
      </div>
      <div style={{ fontSize: 11, color: GREY, marginBottom: 10, lineHeight: 1.5 }}>
        Saved to {titleCase(provName)}'s rules for all future claims — this claim doesn't change. revQ starts applying rules to coding in a coming update.
      </div>
      <RuleComposer providerName={provName} cpts={cpts} icds={icds} rules={{ inc: rr.inc, exc: rr.exc }} tokensMode="always" onAdd={onAdd} onAttempt={onAttempt} saving={rr.saving} err={rr.err} />
      <RuleBand label="Include" list={rr.inc} isInc onFlip={rr.flip} onDel={rr.del} hl={hl} />
      <RuleBand label="Exclude" list={rr.exc} isInc={false} onFlip={rr.flip} onDel={rr.del} hl={hl} />
      {!rr.inc.length && !rr.exc.length ? <div style={{ fontSize: 12, color: GREY, marginTop: 12 }}>No pre-claim rules yet.</div> : null}
    </div>
  );
}

const INSP_BTN = { fontSize: 12, fontWeight: 600, padding: "5px 11px", borderRadius: 9, border: `1px solid ${HAIR}`, background: "#fff", color: INK, cursor: "pointer" };

/* "Pre-Claim Rules" block for the per-line and per-diagnosis inspectors.
   Shows only rules that MENTION this code; buttons prefill the verdict composer. */
export function CodeRulesInspectorBlock({ code, rr, provName, onRulePrefill, onManage }) {
  const mentions = rr.inList ? [
    ...rr.inc.map((t) => ({ t, type: "Include" })),
    ...rr.exc.map((t) => ({ t, type: "Exclude" })),
  ].filter((r) => extractCodes(r.t).codes.includes(code)) : [];
  return (
    <div style={{ marginTop: 16, borderTop: `1px solid ${HAIR}`, paddingTop: 12 }} data-testid="cfv-insp-rules">
      <div className="cfv-rail-lbl" style={{ margin: "0 0 8px" }}>Pre-Claim Rules</div>
      {!rr.inList ? <NotInSetup providerName={provName} onManage={onManage} /> : (
        <>
          {mentions.length ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 10 }}>
              {mentions.map((r, i) => (
                <div key={i} data-testid={`cfv-insp-mention-${i}`} style={{ fontSize: 12, color: INK }}>
                  <span style={{ color: GREY, fontWeight: 600 }}>{r.type} · </span>{r.t}
                </div>
              ))}
            </div>
          ) : <div style={{ fontSize: 12, color: GREY, marginBottom: 10 }}>No rule mentions {code}.</div>}
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" data-testid="cfv-insp-rule-always" onClick={() => onRulePrefill(code, "always")} style={INSP_BTN}>Include rule…</button>
            <button type="button" data-testid="cfv-insp-rule-never" onClick={() => onRulePrefill(code, "never")} style={INSP_BTN}>Exclude rule…</button>
          </div>
        </>
      )}
    </div>
  );
}

/* Guard-trip sheet: a concurrent edit was detected, rules reloaded, offer to restore the unsaved text. */
export function RulesChangedSheet({ onPutBack, onClose, canRestore }) {
  return (
    <div data-testid="cfv-rules-changed-sheet" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, zIndex: 9600, background: "rgba(20,20,18,.4)", backdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ width: "min(420px,94vw)", background: "#fff", borderRadius: 16, border: `1px solid ${HAIR}`, padding: "20px 22px", boxShadow: "0 20px 60px rgba(0,0,0,.25)" }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: INK }}>These rules changed</div>
        <p style={{ fontSize: 13, color: GREY, marginTop: 8, lineHeight: 1.5 }}>Someone updated this provider's rules somewhere else, so we reloaded the latest set. Your rule wasn't saved.</p>
        <div style={{ display: "flex", gap: 10, marginTop: 16, justifyContent: "flex-end" }}>
          <button type="button" data-testid="cfv-rules-changed-dismiss" onClick={onClose} style={{ fontSize: 12.5, fontWeight: 600, padding: "8px 14px", borderRadius: 10, border: `1px solid ${HAIR}`, background: "#fff", color: INK, cursor: "pointer" }}>Got it</button>
          {canRestore ? <button type="button" data-testid="cfv-rules-changed-putback" onClick={onPutBack} style={{ fontSize: 12.5, fontWeight: 600, padding: "8px 14px", borderRadius: 10, border: "none", background: INK, color: "#fff", cursor: "pointer" }}>Put it back in the box</button> : null}
        </div>
      </div>
    </div>
  );
}
